import { useEffect, useMemo, useState } from "react"
import { Box, Checkbox, HStack, IconButton, Input, NativeSelect, Separator, Spinner, Stack, Table } from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { FaPlus, FaSync } from "react-icons/fa"
import { stageOfferInPortal, upsertOfferGrant, upsertDealOffer, updateDealGrant, confirmStagedGrants } from "@/api/deals"
import { Button, Text, toaster } from "@/components"
import { CompanyInfo, DealGrant, DealOffer, EsoDeal, IncentiveType } from "@/types"
import { FaEdit, FaCheck } from "react-icons/fa"
import { goalSeek } from "@/utils"

interface Props {
  deal: EsoDeal
  company: CompanyInfo
  onRefreshGrants?: () => void
  isRefreshingGrants?: boolean
  hideConfigTable?: boolean
}

type GrantItem = {
  id: number
  grantNumber: number
  grantType: string
  description: string | null
  vested: number
  price: string
  grantDate: string | null
  include: boolean
  tax: string
}

const getGrantBg = (type: string) => {
  switch (type.toUpperCase()) {
    case "ISO":
      return "grant.100"
    case "NSO":
      return "grant.200"
    case "RSU":
      return "grant.300"
    case "SHARES":
    case "SHARE":
      return "grant.400"
    default:
      return "grant.100"
  }
}

const formatCurrency = (value: string | number | undefined | null) => {
  if (value === null || value === undefined || value === "") return "—"
  const num = typeof value === "number" ? value : parseFloat(value)
  return isNaN(num) ? String(value) : `$${num.toLocaleString()}`
}

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return "—"
  return new Date(dateString).toLocaleDateString()
}

const GrantCard = ({
  grant,
  mostRecentFmv,
  onToggleInclude,
  onSaveGrant,
}: {
  grant: GrantItem
  mostRecentFmv: number
  onToggleInclude: (grantNumber: number) => void
  onSaveGrant: (grantId: number, data: Record<string, any>) => Promise<void>
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editDesc, setEditDesc] = useState(grant.description || "")
  const [editVested, setEditVested] = useState(String(grant.vested))
  const [editPrice, setEditPrice] = useState(grant.price)
  const [editDate, setEditDate] = useState(grant.grantDate ? grant.grantDate.split("T")[0] : "")
  const [editTax, setEditTax] = useState(grant.tax)
  const [saving, setSaving] = useState(false)

  const strikePrice = parseFloat(grant.price)
  const strikeFmvDiff = mostRecentFmv > 0 && !isNaN(strikePrice)
    ? ((strikePrice - mostRecentFmv) / mostRecentFmv) * 100
    : null

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSaveGrant(grant.id, {
        description: editDesc || null,
        vested: parseInt(editVested) || 0,
        price: parseFloat(editPrice) || 0,
        grantDate: editDate || null,
        ...(grant.grantType.toUpperCase() === "NSO" ? { tax: parseFloat(editTax) || null } : {}),
      })
      setIsEditing(false)
    } catch {
      // error handled by parent
    } finally {
      setSaving(false)
    }
  }

  if (isEditing) {
    return (
      <Stack
        minW="220px"
        p={4}
        borderRadius={10}
        bg={getGrantBg(grant.grantType)}
        boxShadow="md"
        gap={2}
        border="2px solid"
        borderColor="blue.400"
      >
        <HStack justifyContent="space-between">
          <Text fontSize="xs" fontWeight="bold" color="gray.600">{grant.grantType.toUpperCase()}</Text>
          <HStack gap={1}>
            <IconButton aria-label="Save grant" size="xs" variant="ghost" onClick={handleSave} loading={saving}>
              <FaCheck size={10} />
            </IconButton>
          </HStack>
        </HStack>
        <Input size="xs" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} placeholder="Description" />
        <Input size="xs" type="number" value={editVested} onChange={(e) => setEditVested(e.target.value)} placeholder="Vested shares" />
        <Input size="xs" type="number" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} placeholder="Strike price" step="0.0001" />
        <Input size="xs" type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} />
        {grant.grantType.toUpperCase() === "NSO" && (
          <Input size="xs" type="number" value={editTax} onChange={(e) => setEditTax(e.target.value)} placeholder="Tax" />
        )}
      </Stack>
    )
  }

  return (
    <Stack
      minW="200px"
      p={4}
      borderRadius={10}
      bg={getGrantBg(grant.grantType)}
      boxShadow="sm"
      gap={1}
      opacity={grant.include ? 1 : 0.5}
    >
      <HStack justifyContent="space-between">
        <Text fontSize="xs" fontWeight="bold" color="gray.700" truncate maxW="120px">
          {grant.description || `Grant #${grant.grantNumber}`}
        </Text>
        <HStack gap={1}>
          <IconButton aria-label="Edit grant" size="xs" variant="ghost" onClick={() => setIsEditing(true)}>
            <FaEdit size={10} />
          </IconButton>
          <Text fontSize="xs" fontWeight="bold" color="gray.600">
            {grant.grantType.toUpperCase()}
          </Text>
        </HStack>
      </HStack>
      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
        {grant.vested.toLocaleString()} shares
      </Text>
      <HStack gap={2} alignItems="baseline">
        <Text fontSize="xs" color="gray.600">
          Strike: ${strikePrice.toFixed(4)}
        </Text>
        {strikeFmvDiff !== null && (
          <Text
            fontSize="xs"
            fontWeight="bold"
            color={strikeFmvDiff <= 0 ? "green.600" : "red.600"}
          >
            {strikeFmvDiff >= 0 ? "+" : ""}{strikeFmvDiff.toFixed(1)}%
          </Text>
        )}
      </HStack>
      <HStack mt={1} gap={2}>
        <Checkbox.Root
          size="sm"
          checked={grant.include}
          onCheckedChange={() => onToggleInclude(grant.grantNumber)}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control />
        </Checkbox.Root>
        <Text fontSize="xs" color="gray.600">Include</Text>
      </HStack>
    </Stack>
  )
}


const OfferView = ({
  offer,
  offers,
  grants,
  totalIsoExercise,
  totalNsoExercise,
  nsoWithholdingTax,
  mostRecentLrp,
  mostRecentFmv,
  minPpSplit,
  underwritingLrp,
  closingLtvData,
  onSelectOffer,
  onNewOffer,
  onStageOffer,
  onOfferSaved,
  dealId,
  hideConfigTable,
}: {
  offer: DealOffer | null
  offers: DealOffer[]
  grants: GrantItem[]
  totalIsoExercise: number
  totalNsoExercise: number
  nsoWithholdingTax: number
  mostRecentLrp: number
  mostRecentFmv: number
  minPpSplit: number
  underwritingLrp: number | null
  closingLtvData?: { dealId: number; ltv: number }[]
  onSelectOffer: (offerNum: string) => void
  onNewOffer: () => void
  onStageOffer: (offerId: number, isStagedInPortal: boolean) => Promise<void>
  onOfferSaved: () => void
  dealId: number
  hideConfigTable?: boolean
}) => {
  // Staging state — tracks the checkbox value locally
  const [stagedLocal, setStagedLocal] = useState(!!offer?.isStagedInPortal)
  const [pendingConfirm, setPendingConfirm] = useState(false)
  const [isStaging, setIsStaging] = useState(false)

  // Sync local state when offer changes (switching offers or server refetch)
  useEffect(() => {
    setStagedLocal(!!offer?.isStagedInPortal)
    setPendingConfirm(false)
  }, [offer?.id, offer?.isStagedInPortal])

  // Local mutable state for editable fields
  const [offerType, setOfferType] = useState<string>(offer?.offerType || "Traditional")
  const [allocationAgreement, setAllocationAgreement] = useState<string>(offer?.allocationAgreement || "PPA")
  const [preferentialReturn, setPreferentialReturn] = useState<string>(offer?.isPreferentialReturn ? "YES" : "NO")
  const [pp, setPp] = useState<string>(offer?.pp != null ? String(offer.pp) : "")
  const [ppSplit, setPpSplit] = useState<string>(offer?.ppSplit != null ? String(offer.ppSplit) : "")
  const [hypotheticalPriceShare, setHypotheticalPriceShare] = useState<string>(offer?.hypotheticalSharePrice != null ? String(offer.hypotheticalSharePrice) : "")
  const [desiredLiquidity, setDesiredLiquidity] = useState<string>(offer?.desiredLiquidity != null ? String(offer.desiredLiquidity) : "")
  const [optionsDocFee, setOptionsDocFee] = useState<string>(offer?.optionsDocFee != null ? String(offer.optionsDocFee) : "0")
  const [upFrontFee, setUpFrontFee] = useState<string>(offer?.upFrontFee != null ? String(offer.upFrontFee) : "0")
  const [exchangeRate, setExchangeRate] = useState<string>(offer?.exchangeRate != null ? String(offer.exchangeRate) : "1")

  // Sync local state when selected offer changes
  useEffect(() => {
    setOfferType(offer?.offerType || "Traditional")
    setAllocationAgreement(offer?.allocationAgreement || "PPA")
    setPreferentialReturn(offer?.isPreferentialReturn ? "YES" : "NO")
    setPp(offer?.pp != null ? String(offer.pp) : "")
    setPpSplit(offer?.ppSplit != null ? String(offer.ppSplit) : "")
    setHypotheticalPriceShare(offer?.hypotheticalSharePrice != null ? String(offer.hypotheticalSharePrice) : "")
    setDesiredLiquidity(offer?.desiredLiquidity != null ? String(offer.desiredLiquidity) : "")
    setOptionsDocFee(offer?.optionsDocFee != null ? String(offer.optionsDocFee) : "0")
    setUpFrontFee(offer?.upFrontFee != null ? String(offer.upFrontFee) : "0")
    setExchangeRate(offer?.exchangeRate != null ? String(offer.exchangeRate) : "1")
    setIncentivePp(offer?.incentivePp != null ? String(offer.incentivePp) : "0.1")
    setIncentivePpSplit(offer?.incentivePpSplit != null ? String(offer.incentivePpSplit) : "")
    setIncentiveDeadline(offer?.incentiveDeadline || "")
    setIncentiveLp(offer?.incentiveLp != null ? String(offer.incentiveLp) : "")
    setShowIncentive(!!offer?.isIncentive)
  }, [offer])

  // Pledged shares by type
  const totalIsosPledged = useMemo(() => {
    return grants.filter((g) => g.grantType.toUpperCase() === "ISO" && g.include).reduce((sum, g) => sum + g.vested, 0)
  }, [grants])
  const totalNsosPledged = useMemo(() => {
    return grants.filter((g) => g.grantType.toUpperCase() === "NSO" && g.include).reduce((sum, g) => sum + g.vested, 0)
  }, [grants])
  const totalSharesTypePledged = useMemo(() => {
    return grants.filter((g) => g.grantType.toUpperCase() === "SHARES" && g.include).reduce((sum, g) => sum + g.vested, 0)
  }, [grants])
  const totalSharesPledged = totalIsosPledged + totalNsosPledged + totalSharesTypePledged

  // Computed derived values
  const initialPayment = totalIsoExercise + totalNsoExercise + nsoWithholdingTax
  const desiredLiquidityNum = parseFloat(desiredLiquidity) || 0
  const optionsDocFeeNum = parseFloat(optionsDocFee) || 0
  const upFrontFeeNum = parseFloat(upFrontFee) || 0
  const ppNum = parseFloat(pp) || 0
  const ppSplitNum = parseFloat(ppSplit) || 0

  const totalUpfrontPayment = initialPayment + desiredLiquidityNum

  // TODO: yearsToLiquidityDealManagerEstimate not available in new backend
  const goLongHurdle = 4 * minPpSplit + 2 * (1 - minPpSplit)

  const computedLtv = useMemo(() => {
    if (totalSharesPledged === 0) return 0
    if (allocationAgreement === "RSU") {
      return ppNum * totalSharesPledged === 0 ? 0 : totalUpfrontPayment / (ppNum * totalSharesPledged)
    }
    return totalUpfrontPayment / totalSharesPledged
  }, [totalSharesPledged, allocationAgreement, ppNum, totalUpfrontPayment])

  const ltvRank = useMemo(() => {
    if (!closingLtvData || closingLtvData.length === 0 || computedLtv <= 0) return null
    const sorted = [...closingLtvData].map((d) => d.ltv).sort((a, b) => a - b)
    const rank = sorted.filter((ltv) => ltv <= computedLtv).length
    return { rank, total: sorted.length }
  }, [closingLtvData, computedLtv])

  const optioneePp = 1 - ppNum
  const subjectToPp = totalUpfrontPayment * ppSplitNum
  const subjectToLp2x = totalUpfrontPayment - subjectToPp

  const AA_TAX_ADJUSTMENT = (1 - 0.2) / (1 - 0.37)
  const lpBeforeTax = subjectToPp * optioneePp + subjectToLp2x * 2

  const computedLp = useMemo(() => {
    if (allocationAgreement === "AA") {
      return lpBeforeTax * AA_TAX_ADJUSTMENT - totalUpfrontPayment * (AA_TAX_ADJUSTMENT - 1)
    } else if (preferentialReturn === "YES" || allocationAgreement === "RSU") {
      return lpBeforeTax
    } else {
      return Math.max(lpBeforeTax, optioneePp * totalUpfrontPayment)
    }
  }, [allocationAgreement, preferentialReturn, lpBeforeTax, totalUpfrontPayment, optioneePp])

  const lpMultiple = optioneePp && totalUpfrontPayment
    ? computedLp / (optioneePp * totalUpfrontPayment)
    : 0

  // "How Much Would We Make" computed values
  const hypotheticalPriceShareNum = parseFloat(hypotheticalPriceShare) || 0
  const grossProceeds = hypotheticalPriceShareNum * totalSharesPledged
  const esoParticipation = grossProceeds * ppNum
  const netAfterEsoParticipation = Math.max(grossProceeds - esoParticipation, 0)
  const liquidationPreference = allocationAgreement === "RSU" ? 0 : Math.min(computedLp, netAfterEsoParticipation)
  const totalEsoProceeds = useMemo(() => {
    if (allocationAgreement !== "RSU") {
      const minimumReturn = 0
      const calculatedValue = liquidationPreference + esoParticipation
      const adjustedValue = Math.max(calculatedValue, minimumReturn)
      return Math.min(adjustedValue, grossProceeds)
    } else {
      return esoParticipation
    }
  }, [allocationAgreement, liquidationPreference, esoParticipation, grossProceeds])
  const backendNetProceedsToOptionee = Math.max(grossProceeds - totalEsoProceeds, 0)
  const optioneeNetProceeds = totalUpfrontPayment + backendNetProceedsToOptionee - totalIsoExercise - totalNsoExercise - optionsDocFeeNum - upFrontFeeNum
  const totalEsoProfit = totalEsoProceeds - totalUpfrontPayment + optionsDocFeeNum + upFrontFeeNum
  const esoRoi = totalUpfrontPayment > 0 ? totalEsoProceeds / totalUpfrontPayment : null

  // Chart data for "How Much Would We Make"
  const wmwmChartData = useMemo(() => {
    if (totalSharesPledged === 0) return []

    const computeAtPrice = (price: number) => {
      const gp = price * totalSharesPledged
      const esoPart = gp * ppNum
      const netAfterEso = Math.max(gp - esoPart, 0)
      const liqPref = allocationAgreement === "RSU" ? 0 : Math.min(computedLp, netAfterEso)
      let tep: number
      if (allocationAgreement !== "RSU") {
        tep = Math.min(Math.max(liqPref + esoPart, 0), gp)
      } else {
        tep = esoPart
      }
      const backendNet = Math.max(gp - tep, 0)
      const onp = totalUpfrontPayment + backendNet - totalIsoExercise - totalNsoExercise - optionsDocFeeNum - upFrontFeeNum
      const tepProfit = tep - totalUpfrontPayment + optionsDocFeeNum + upFrontFeeNum
      return { totalEsoProfit: tepProfit, optioneeNetProceeds: onp }
    }

    // Find breakeven price where ESO breaks even: totalEsoProfit = 0
    let lo = 0, hi = 100
    while (computeAtPrice(hi).totalEsoProfit < 0 && hi < 1000000) hi *= 2
    for (let i = 0; i < 64; i++) {
      const mid = (lo + hi) / 2
      if (computeAtPrice(mid).totalEsoProfit < 0) lo = mid
      else hi = mid
    }
    const breakeven = (lo + hi) / 2

    // Generate x-axis prices: 0, breakeven, then increasing intervals
    const interval = breakeven > 0 ? breakeven : 10
    const prices = [0, breakeven]
    for (let mult = 2; mult <= 6; mult++) {
      prices.push(interval * mult)
    }

    return prices.map((price) => {
      const { totalEsoProfit: profit, optioneeNetProceeds: onp } = computeAtPrice(price)
      return {
        price: price,
        priceLabel: `$${price.toFixed(2)}`,
        optioneeNetProceeds: Math.round(onp),
        totalEsoProfit: Math.round(profit),
      }
    })
  }, [totalSharesPledged, ppNum, allocationAgreement, computedLp, totalUpfrontPayment, totalIsoExercise, totalNsoExercise, optionsDocFeeNum, upFrontFeeNum])

  const [showIncentive, setShowIncentive] = useState(!!offer?.isIncentive)
  const [incentivePp, setIncentivePp] = useState<string>(offer?.incentivePp != null ? String(offer.incentivePp) : "0.1")
  const [incentivePpSplit, setIncentivePpSplit] = useState<string>(offer?.incentivePpSplit != null ? String(offer.incentivePpSplit) : "")
  const [incentiveDeadline, setIncentiveDeadline] = useState<string>(offer?.incentiveDeadline || "")
  const [incentiveLp, setIncentiveLp] = useState<string>(offer?.incentiveLp != null ? String(offer.incentiveLp) : "")

  const [showLpDetails, setShowLpDetails] = useState(false)
  const [showWmwmDetails, setShowWmwmDetails] = useState(false)
  const [wmwmViewIncentive, setWmwmViewIncentive] = useState(false)
  const [savingOffer, setSavingOffer] = useState<number | null>(null)

  const gatherOfferParams = () => ({
    offerType,
    allocationAgreement,
    pp: parseFloat(pp) || 0,
    ppSplit: parseFloat(ppSplit) || 0,
    isPreferentialReturn: preferentialReturn === "YES" ? 1 : 0,
    desiredLiquidity: parseFloat(desiredLiquidity) || null,
    optionsDocFee: parseFloat(optionsDocFee) || 0,
    upFrontFee: parseFloat(upFrontFee) || 0,
    exchangeRate: parseFloat(exchangeRate) || 1,
    hypotheticalSharePrice: parseFloat(hypotheticalPriceShare) || 0,
    ltv: computedLtv,
    isIncentive: showIncentive ? 1 : 0,
    incentivePp: showIncentive ? parseFloat(incentivePp) || null : null,
    incentivePpSplit: showIncentive ? parseFloat(incentivePpSplit) || null : null,
    incentiveLp: showIncentive ? parseFloat(incentiveLp) || null : null,
    incentiveDeadline: showIncentive ? incentiveDeadline || null : null,
  })

  const gatherGrants = () =>
    grants.map((g) => ({ grantId: g.id, included: g.include ? 1 : 0 }))

  const handleSaveOffer = async (offerNumber: number) => {
    setSavingOffer(offerNumber)
    try {
      await upsertDealOffer(dealId, offerNumber, gatherOfferParams(), gatherGrants())
      toaster.create({ title: `Offer ${offerNumber} saved`, type: "success" })
      onOfferSaved()
    } catch {
      toaster.create({ title: `Failed to save offer ${offerNumber}`, type: "error" })
    } finally {
      setSavingOffer(null)
    }
  }

  const handleStageCurrentOffer = async () => {
    setSavingOffer(0)
    try {
      await upsertDealOffer(dealId, 0, gatherOfferParams(), gatherGrants())
      toaster.create({ title: "Current offer staged (offer 0)", type: "success" })
      onOfferSaved()
    } catch {
      toaster.create({ title: "Failed to stage current offer", type: "error" })
    } finally {
      setSavingOffer(null)
    }
  }

  // Incentive WMWM computed values
  const incentivePpNum = parseFloat(incentivePp) || 0
  const incentiveLpNum = parseFloat(incentiveLp) || 0
  const iEsoParticipation = grossProceeds * incentivePpNum
  const iNetAfterEsoParticipation = Math.max(grossProceeds - iEsoParticipation, 0)
  const iLiquidationPreference = allocationAgreement === "RSU" ? 0 : Math.min(incentiveLpNum, iNetAfterEsoParticipation)
  const iTotalEsoProceeds = useMemo(() => {
    if (allocationAgreement !== "RSU") {
      const calculatedValue = iLiquidationPreference + iEsoParticipation
      return Math.min(Math.max(calculatedValue, 0), grossProceeds)
    }
    return iEsoParticipation
  }, [allocationAgreement, iLiquidationPreference, iEsoParticipation, grossProceeds])
  const iBackendNetProceedsToOptionee = Math.max(grossProceeds - iTotalEsoProceeds, 0)
  const iOptioneeNetProceeds = totalUpfrontPayment + iBackendNetProceedsToOptionee - totalIsoExercise - totalNsoExercise - optionsDocFeeNum - upFrontFeeNum
  const iTotalEsoProfit = iTotalEsoProceeds - totalUpfrontPayment + optionsDocFeeNum + upFrontFeeNum
  const iEsoRoi = totalUpfrontPayment > 0 ? iTotalEsoProceeds / totalUpfrontPayment : null

  // Incentive chart data
  const wmwmIncentiveChartData = useMemo(() => {
    if (totalSharesPledged === 0 || !showIncentive) return []
    const iPp = parseFloat(incentivePp) || 0
    const iLp = parseFloat(incentiveLp) || 0

    const computeAtPrice = (price: number) => {
      const gp = price * totalSharesPledged
      const esoPart = gp * iPp
      const netAfterEso = Math.max(gp - esoPart, 0)
      const liqPref = allocationAgreement === "RSU" ? 0 : Math.min(iLp, netAfterEso)
      let tep: number
      if (allocationAgreement !== "RSU") {
        tep = Math.min(Math.max(liqPref + esoPart, 0), gp)
      } else {
        tep = esoPart
      }
      const backendNet = Math.max(gp - tep, 0)
      const onp = totalUpfrontPayment + backendNet - totalIsoExercise - totalNsoExercise - optionsDocFeeNum - upFrontFeeNum
      const tepProfit = tep - totalUpfrontPayment + optionsDocFeeNum + upFrontFeeNum
      return { totalEsoProfit: tepProfit, optioneeNetProceeds: onp }
    }

    let lo = 0, hi = 100
    while (computeAtPrice(hi).totalEsoProfit < 0 && hi < 1000000) hi *= 2
    for (let i = 0; i < 64; i++) {
      const mid = (lo + hi) / 2
      if (computeAtPrice(mid).totalEsoProfit < 0) lo = mid
      else hi = mid
    }
    const breakeven = (lo + hi) / 2

    const interval = breakeven > 0 ? breakeven : 10
    const prices = [0, breakeven]
    for (let mult = 2; mult <= 6; mult++) {
      prices.push(interval * mult)
    }

    return prices.map((price) => {
      const { totalEsoProfit: profit, optioneeNetProceeds: onp } = computeAtPrice(price)
      return {
        price: price,
        priceLabel: `$${price.toFixed(2)}`,
        optioneeNetProceeds: Math.round(onp),
        totalEsoProfit: Math.round(profit),
      }
    })
  }, [totalSharesPledged, incentivePp, incentiveLp, allocationAgreement, totalUpfrontPayment, totalIsoExercise, totalNsoExercise, optionsDocFeeNum, upFrontFeeNum, showIncentive])

  // WMWM display values (switch between primary and incentive)
  const wmwmEsoParticipation = wmwmViewIncentive ? iEsoParticipation : esoParticipation
  const wmwmNetAfterEso = wmwmViewIncentive ? iNetAfterEsoParticipation : netAfterEsoParticipation
  const wmwmLiqPref = wmwmViewIncentive ? iLiquidationPreference : liquidationPreference
  const wmwmTotalEsoProceeds = wmwmViewIncentive ? iTotalEsoProceeds : totalEsoProceeds
  const wmwmBackendNet = wmwmViewIncentive ? iBackendNetProceedsToOptionee : backendNetProceedsToOptionee
  const wmwmOptioneeNet = wmwmViewIncentive ? iOptioneeNetProceeds : optioneeNetProceeds
  const wmwmTotalEsoProfit = wmwmViewIncentive ? iTotalEsoProfit : totalEsoProfit
  const wmwmEsoRoi = wmwmViewIncentive ? iEsoRoi : esoRoi
  const wmwmChartDataActive = wmwmViewIncentive ? wmwmIncentiveChartData : wmwmChartData

  const handleSet1xDealClick = (newPp?: number) => {
    const ppValue = newPp !== undefined ? newPp : ppNum
    if (newPp !== undefined) {
      setPp(String(newPp))
    }
    setPreferentialReturn("NO")
    setPpSplit(String(1 / (1 + ppValue)))
  }

  const handleSetIncentiveLpx2x = () => {
    const targetLP = 2 * totalUpfrontPayment * (1 - Number(incentivePp))
    console.log("Calculating incentive with LPx = 2x:", { targetLP, totalUpfrontPayment, incentivePp })

    // This represents your IncentiveLP_BeforeTax formula
    function incentiveLPBeforeTax(split: number): number {
      // 👇 Replace this with your real formula
      return split * totalUpfrontPayment
    }

    const incentivePPSplit = goalSeek(
      incentiveLPBeforeTax,
      targetLP,
      { min: 0, max: 1 }
    )

    setIncentivePpSplit(String(incentivePPSplit))
    setIncentiveLp(String(targetLP))
  }

  const handleSetILpxHurdleDeal = () => {
    // TODO: implement iLPx = Hurdle Deal logic
  }

  const INCENTIVE_DEADLINE_LABELS: Record<string, string> = {
    [IncentiveType.THREE_MONTHS]: "3 Months",
    [IncentiveType.SIX_MONTHS]: "6 Months",
    [IncentiveType.NINE_MONTHS]: "9 Months",
    [IncentiveType.ONE_YEAR]: "1 Year",
    [IncentiveType.TWO_YEARS]: "2 Years",
  }

  const handleMinimumMinLpDealClick = () => {
    if (totalSharesPledged === 0 || !underwritingLrp) return
    console.log("Calculating minimum minLP deal with 1x iLPx:")
    console.log("Total Upfront Payment:", totalUpfrontPayment)
    console.log("Go Long Hurdle:", goLongHurdle)
    console.log("Total Shares Pledged:", totalSharesPledged)
    console.log("Underwriting LRP:", underwritingLrp)

    const minPpForILP1x = (totalUpfrontPayment * (goLongHurdle - 1)) / (totalSharesPledged * underwritingLrp * 2)
    
    handleSet1xDealClick(minPpForILP1x)
  }

  return (
    <Box>
      <HStack mb={4} justifyContent="space-between">
        <HStack gap={3}>
          <Text fontSize="lg" fontWeight="bold" whiteSpace="nowrap">
            Offer Generator
          </Text>
          {offer && !offers.some((o) => o.offerNumber === offer.offerNumber) && (
            <Text fontSize="xs" fontWeight="bold" color="orange.600" bg="orange.100" px={2} py={0.5} borderRadius="md" _dark={{ color: "orange.300", bg: "orange.900" }}>
              Draft
            </Text>
          )}
          <NativeSelect.Root size="sm" w="200px">
            <NativeSelect.Field
              _dark={{ borderColor: "whiteAlpha.500" }}
              value={offer ? String(offer.offerNumber) : "new"}
              onChange={(e) => onSelectOffer(e.target.value)}
            >
              {offers.map((o) => (
                <option key={o.offerNumber} value={String(o.offerNumber)}>
                  Offer {o.offerNumber} — {o.allocationAgreement || "PPA"}{o.isStaged ? " (Staged)" : ""}{o.isStagedInPortal ? " (Portal)" : ""}
                </option>
              ))}
              <option value="new">New Offer</option>
            </NativeSelect.Field>
          </NativeSelect.Root>
          <Checkbox.Root
            size="sm"
            checked={stagedLocal}
            disabled={!offer || offer.id === 0}
            onCheckedChange={(details) => {
              const checked = !!details.checked
              setStagedLocal(checked)
              if (checked) {
                // Stage: require confirm
                setPendingConfirm(true)
              } else {
                // Unstage: fire immediately
                setPendingConfirm(false)
                if (offer && offer.id !== 0) {
                  setIsStaging(true)
                  onStageOffer(offer.id, false).finally(() => setIsStaging(false))
                }
              }
            }}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>Stage in Portal</Checkbox.Label>
          </Checkbox.Root>
          {pendingConfirm && offer && offer.id !== 0 && (
            <Button
              size="xs"
              colorPalette="green"
              variant="solid"
              loading={isStaging}
              onClick={() => {
                setIsStaging(true)
                onStageOffer(offer.id, true)
                  .then(() => setPendingConfirm(false))
                  .finally(() => setIsStaging(false))
              }}
            >
              Confirm
            </Button>
          )}
        </HStack>
        <Button size="sm" variant="outline" onClick={onNewOffer} disabled={offer != null && !offers.some((o) => o.offerNumber === offer.offerNumber)} _dark={{ borderColor: "whiteAlpha.500" }}>
          <FaPlus size={10} />
          <Text ml={1}>New Offer</Text>
        </Button>
      </HStack>

      {offer && (
        <HStack gap={2} mt={2} flexWrap="wrap">
          <Button
            size="xs"
            variant="solid"
            colorPalette="green"
            loading={savingOffer === 0}
            disabled={savingOffer !== null}
            onClick={handleStageCurrentOffer}
          >
            Stage Current Offer
          </Button>
          {[1, 2, 3, 4, 5].map((num) => (
            <Button
              key={num}
              size="xs"
              variant="outline"
              colorPalette="blue"
              loading={savingOffer === num}
              disabled={savingOffer !== null}
              onClick={() => handleSaveOffer(num)}
            >
              Save Offer {num}
            </Button>
          ))}
        </HStack>
      )}

      <Separator my={4} />

      <HStack alignItems="flex-start" gap={6} flexWrap={{ base: "wrap", lg: "nowrap" }}>
        {/* Left: Offer params + Breakdown — hidden when config table is moved to Overview */}
        {!hideConfigTable && <Box flex={1} minW="300px">
          {!hideConfigTable && (
            <Table.Root size="sm">
              <Table.Body>
                <Table.Row bg="orange.50" _dark={{ bg: "orange.950" }}>
                  <Table.Cell fontWeight="semibold" w="200px">Offer Type</Table.Cell>
                  <Table.Cell>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field value={offerType || "Traditional"} onChange={(e) => setOfferType(e.target.value)}>
                        <option value="Traditional">Traditional</option>
                        <option value="Transfer">Transfer</option>
                        <option value="Forward Contract">Forward Contract</option>
                        <option value="Deemed Sale w/Buy Back">Deemed Sale w/Buy Back</option>
                        <option value="IPO Lockup or Term Loan">IPO Lockup or Term Loan</option>
                        <option value="RSU">RSU</option>
                      </NativeSelect.Field>
                    </NativeSelect.Root>
                  </Table.Cell>
                </Table.Row>
                <Table.Row bg="orange.50" _dark={{ bg: "orange.950" }}>
                  <Table.Cell fontWeight="semibold">Allocation Agreement</Table.Cell>
                  <Table.Cell>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field value={allocationAgreement} onChange={(e) => setAllocationAgreement(e.target.value)}>
                        <option value="PPA">PPA</option>
                        <option value="AA">AA</option>
                        <option value="RSU">RSU</option>
                        <option value="IPO">IPO</option>
                        <option value="Transfer">Transfer</option>
                        <option value="Direct">Direct</option>
                        <option value="Forward">Forward</option>
                      </NativeSelect.Field>
                    </NativeSelect.Root>
                  </Table.Cell>
                </Table.Row>
                <Table.Row bg="orange.50" _dark={{ bg: "orange.950" }}>
                  <Table.Cell fontWeight="semibold">Preferential Return</Table.Cell>
                  <Table.Cell>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field value={preferentialReturn} onChange={(e) => setPreferentialReturn(e.target.value)}>
                        <option value="YES">Yes</option>
                        <option value="NO">No</option>
                      </NativeSelect.Field>
                    </NativeSelect.Root>
                  </Table.Cell>
                </Table.Row>
                <Table.Row bg="orange.50" _dark={{ bg: "orange.950" }}>
                  <Table.Cell fontWeight="semibold">Desired Liquidity</Table.Cell>
                  <Table.Cell>
                    <Input size="sm" variant="flushed" type="number" value={desiredLiquidity} onChange={(e) => setDesiredLiquidity(e.target.value)} placeholder="0" />
                  </Table.Cell>
                </Table.Row>
                <Table.Row bg="orange.50" _dark={{ bg: "orange.950" }}>
                  <Table.Cell fontWeight="semibold">Options Doc Fee</Table.Cell>
                  <Table.Cell>
                    <Input size="sm" variant="flushed" type="number" value={optionsDocFee} onChange={(e) => setOptionsDocFee(e.target.value)} placeholder="0" />
                  </Table.Cell>
                </Table.Row>
                <Table.Row bg="orange.50" _dark={{ bg: "orange.950" }}>
                  <Table.Cell fontWeight="semibold">Up Front Fee</Table.Cell>
                  <Table.Cell>
                    <Input size="sm" variant="flushed" type="number" value={upFrontFee} onChange={(e) => setUpFrontFee(e.target.value)} placeholder="0" />
                  </Table.Cell>
                </Table.Row>
                <Table.Row bg="orange.50" _dark={{ bg: "orange.950" }}>
                  <Table.Cell fontWeight="semibold">Exchange Rate</Table.Cell>
                  <Table.Cell>
                    <Input size="sm" variant="flushed" type="number" value={exchangeRate} onChange={(e) => setExchangeRate(e.target.value)} placeholder="1" />
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table.Root>
          )}

          <Box
            mt={4}
            cursor="pointer"
            onClick={() => setShowLpDetails(!showLpDetails)}
            p={3}
            borderRadius="md"
            border="1px"
            borderColor="border.default"
            _hover={{ bg: "hover.bg" }}
          >
            <Text fontSize="xs" fontWeight="semibold" color="text.muted" mb={showLpDetails ? 2 : 0}>
              {showLpDetails ? "▾" : "▸"} Breakdown
            </Text>
            {showLpDetails && (
              <Stack gap={1} mt={1}>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">ISOs Pledged</Text>
                  <Text fontSize="xs" fontWeight="semibold">{totalIsosPledged.toLocaleString()}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">NSOs Pledged</Text>
                  <Text fontSize="xs" fontWeight="semibold">{totalNsosPledged.toLocaleString()}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">Shares Pledged</Text>
                  <Text fontSize="xs" fontWeight="semibold">{totalSharesTypePledged.toLocaleString()}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">Total Shares Pledged</Text>
                  <Text fontSize="xs" fontWeight="semibold">{totalSharesPledged.toLocaleString()}</Text>
                </HStack>
                <Separator my={1} />
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">Total ISO Exercise</Text>
                  <Text fontSize="xs" fontWeight="semibold">{formatCurrency(totalIsoExercise)}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">Total NSO Exercise</Text>
                  <Text fontSize="xs" fontWeight="semibold">{formatCurrency(totalNsoExercise)}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">NSO Withholding Tax</Text>
                  <Text fontSize="xs" fontWeight="semibold">{formatCurrency(nsoWithholdingTax)}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">Initial Payment</Text>
                  <Text fontSize="xs" fontWeight="semibold">{formatCurrency(initialPayment)}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">Total Upfront Payment</Text>
                  <Text fontSize="xs" fontWeight="semibold">{formatCurrency(totalUpfrontPayment)}</Text>
                </HStack>
                <Separator my={1} />
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">Optionee PP</Text>
                  <Text fontSize="xs" fontWeight="semibold">{optioneePp.toFixed(4)}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">Subject to PP</Text>
                  <Text fontSize="xs" fontWeight="semibold">{formatCurrency(subjectToPp)}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">Subject to LP 2x</Text>
                  <Text fontSize="xs" fontWeight="semibold">{formatCurrency(subjectToLp2x)}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">LP Before Tax</Text>
                  <Text fontSize="xs" fontWeight="semibold">{formatCurrency(lpBeforeTax)}</Text>
                </HStack>
                <Separator my={1} />
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">Min PP Split</Text>
                  <Text fontSize="xs" fontWeight="semibold">{minPpSplit.toFixed(4)}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">Go Long Hurdle</Text>
                  <Text fontSize="xs" fontWeight="semibold">{goLongHurdle.toFixed(4)}</Text>
                </HStack>
              </Stack>
            )}
          </Box>
        </Box>}

        {/* Right: Action buttons + Deal params */}
        <Stack flex={1} minW="300px" gap={4}>
          <HStack gap={2} flexWrap="wrap">
            <Button
              size="xs"
              variant="solid"
              disabled={!offer}
              onClick={() => handleSet1xDealClick()}
              _dark={{ borderColor: "whiteAlpha.500" }}
            >
              Set 1x iLPx Deal
            </Button>
            <Button
              size="xs"
              variant="solid"
              disabled={!offer}
              onClick={handleMinimumMinLpDealClick}
              _dark={{ borderColor: "whiteAlpha.500" }}
            >
              Set Minimum minLP Deal with 1x iLPx
            </Button>
          </HStack>

          {/* Primary: PP + PP Split */}
          <HStack gap={4}>
            <Stack gap={1} flex={1} p={4} borderRadius="lg" bg="orange.50" boxShadow="md" _dark={{ bg: "orange.950" }}>
              <Text fontSize="xs" color="text.muted" fontWeight="medium">PP</Text>
              <Input size="md" variant="flushed" fontWeight="bold" fontSize="xl" value={pp} onChange={(e) => setPp(e.target.value)} placeholder="0" />
              {ppNum > 0 && <Text fontSize="xs" color="text.muted">{(ppNum * 100).toFixed(1)}%</Text>}
            </Stack>
            <Stack gap={1} flex={1} p={4} borderRadius="lg" bg="orange.50" boxShadow="md" _dark={{ bg: "orange.950" }}>
              <Text fontSize="xs" color="text.muted" fontWeight="medium">PP Split</Text>
              <Input size="md" variant="flushed" fontWeight="bold" fontSize="xl" value={ppSplit} onChange={(e) => setPpSplit(e.target.value)} placeholder="0" />
              {ppSplitNum > 0 && <Text fontSize="xs" color="text.muted">{(ppSplitNum * 100).toFixed(1)}%</Text>}
            </Stack>
          </HStack>

          {/* Primary: LP/LPx + LTV */}
          <HStack gap={4}>
            <Stack flex={1} p={4} borderRadius="lg" bg="card.bg" boxShadow="md" gap={3}>
              <HStack justifyContent="space-between">
                <Text fontSize="sm" fontWeight="medium" color="text.muted">LP</Text>
                <Text fontSize="xl" fontWeight="bold">{formatCurrency(computedLp)}</Text>
              </HStack>
              <Separator />
              <HStack justifyContent="space-between">
                <Text fontSize="sm" fontWeight="medium" color="text.muted">LP Multiple</Text>
                <Text fontSize="xl" fontWeight="bold">{lpMultiple ? `${lpMultiple.toFixed(4)}x` : "—"}</Text>
              </HStack>
            </Stack>
            <Stack gap={1} flex={1} p={4} borderRadius="lg" bg="card.bg" boxShadow="md">
              <Text fontSize="xs" color="text.muted" fontWeight="medium">LTV</Text>
              <Text fontSize="xl" fontWeight="bold">{computedLtv ? `$${computedLtv.toFixed(2)}` : "—"}</Text>
              {computedLtv > 0 && (
                <HStack gap={3}>
                  {mostRecentFmv > 0 && (
                    <Text fontSize="xs" color="text.muted">{((computedLtv / mostRecentFmv) * 100).toFixed(1)}% of FMV</Text>
                  )}
                  {mostRecentLrp > 0 && (
                    <Text fontSize="xs" color="text.muted">{((computedLtv / mostRecentLrp) * 100).toFixed(1)}% of LRP</Text>
                  )}
                </HStack>
              )}
              {ltvRank && (
                <Text fontSize="xs" color="text.muted" fontWeight="medium">
                  Rank: {ltvRank.rank} / {ltvRank.total} (closing deals)
                </Text>
              )}
            </Stack>
          </HStack>

          {/* Add/Remove Incentive Deal + Incentive action buttons */}
          <HStack gap={2} flexWrap="wrap">
            <Button
              size="xs"
              variant="outline"
              colorPalette={showIncentive ? "red" : "purple"}
              onClick={() => { setShowIncentive(!showIncentive); if (showIncentive) setWmwmViewIncentive(false) }}
              disabled={!offer}
            >
              {showIncentive ? "Remove Incentive Deal" : "Add Incentive Deal"}
            </Button>
            {showIncentive && (
              <>
                <Button
                  size="xs"
                  variant="solid"
                  disabled={!offer}
                  onClick={handleSetIncentiveLpx2x}
                  _dark={{ borderColor: "whiteAlpha.500" }}
                >
                  Set Incentive LPx = 2x
                </Button>
                <Button
                  size="xs"
                  variant="solid"
                  disabled={!offer}
                  onClick={handleSetILpxHurdleDeal}
                  _dark={{ borderColor: "whiteAlpha.500" }}
                >
                  Set iLPx = Hurdle Deal
                </Button>
              </>
            )}
          </HStack>

          {/* Incentive sections */}
          {showIncentive && (
            <>
              <HStack gap={4}>
                <Stack gap={1} flex={1} p={4} borderRadius="lg" bg="purple.50" boxShadow="md" _dark={{ bg: "purple.950" }}>
                  <Text fontSize="xs" color="text.muted" fontWeight="medium">Incentive PP</Text>
                  <Input size="md" variant="flushed" fontWeight="bold" fontSize="xl" value={incentivePp} onChange={(e) => setIncentivePp(e.target.value)} placeholder="0" />
                  {parseFloat(incentivePp) > 0 && <Text fontSize="xs" color="text.muted">{(parseFloat(incentivePp) * 100).toFixed(1)}%</Text>}
                </Stack>
                <Stack gap={1} flex={1} p={4} borderRadius="lg" bg="purple.50" boxShadow="md" _dark={{ bg: "purple.950" }}>
                  <Text fontSize="xs" color="text.muted" fontWeight="medium">Incentive PP Split</Text>
                  <Input size="md" variant="flushed" fontWeight="bold" fontSize="xl" value={incentivePpSplit} onChange={(e) => setIncentivePpSplit(e.target.value)} placeholder="0" />
                  {parseFloat(incentivePpSplit) > 0 && <Text fontSize="xs" color="text.muted">{(parseFloat(incentivePpSplit) * 100).toFixed(1)}%</Text>}
                </Stack>
                <Stack gap={1} flex={1} p={4} borderRadius="lg" bg="card.bg" boxShadow="md">
                  <Text fontSize="xs" color="text.muted" fontWeight="medium">Incentive Deadline</Text>
                  <NativeSelect.Root size="sm">
                    <NativeSelect.Field value={incentiveDeadline} onChange={(e) => setIncentiveDeadline(e.target.value)}>
                      <option value="">Select...</option>
                      {Object.entries(INCENTIVE_DEADLINE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </NativeSelect.Field>
                  </NativeSelect.Root>
                </Stack>
              </HStack>

              <Stack p={4} borderRadius="lg" bg="purple.50" boxShadow="md" gap={3} _dark={{ bg: "purple.950" }}>
                <HStack justifyContent="space-between">
                  <Text fontSize="sm" fontWeight="medium" color="text.muted">Incentive LP</Text>
                  <Input
                    size="md"
                    variant="flushed"
                    fontWeight="bold"
                    fontSize="xl"
                    textAlign="right"
                    maxW="200px"
                    value={incentiveLp}
                    onChange={(e) => setIncentiveLp(e.target.value)}
                    placeholder="0"
                  />
                </HStack>
                {parseFloat(incentiveLp) > 0 && totalUpfrontPayment > 0 && (
                  <>
                    <Separator />
                    <HStack justifyContent="space-between">
                      <Text fontSize="sm" fontWeight="medium" color="text.muted">Incentive Multiple</Text>
                      <Text fontSize="xl" fontWeight="bold">
                        {(parseFloat(incentiveLp) / ((1 - (parseFloat(incentivePp) || 0)) * totalUpfrontPayment)).toFixed(4)}x
                      </Text>
                    </HStack>
                  </>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </HStack>

      <Separator my={6} />

      {/* How Much Would We Make */}
      <HStack mt={6} mb={4} gap={3} alignItems="center">
        <Text fontSize="lg" fontWeight="bold">
          How Much Would We Make
        </Text>
        {showIncentive && (
          <Button
            size="xs"
            variant="outline"
            colorPalette={wmwmViewIncentive ? "purple" : "blue"}
            onClick={() => setWmwmViewIncentive(!wmwmViewIncentive)}
          >
            {wmwmViewIncentive ? "Viewing Incentive" : "Viewing Primary"}
          </Button>
        )}
      </HStack>
      <HStack alignItems="flex-start" gap={6} flexWrap={{ base: "wrap", lg: "nowrap" }}>
        <Stack flex={1} minW="300px" gap={3}>
          <Table.Root size="sm">
            <Table.Body>
              <Table.Row bg={wmwmViewIncentive ? "purple.50" : "orange.50"} _dark={{ bg: wmwmViewIncentive ? "purple.950" : "orange.950" }}>
                <Table.Cell fontWeight="semibold" w="280px">Hypothetical Price/Share</Table.Cell>
                <Table.Cell>
                  <Input size="sm" variant="flushed" type="number" value={hypotheticalPriceShare} onChange={(e) => setHypotheticalPriceShare(e.target.value)} placeholder="Price/Share" />
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell fontWeight="semibold">Gross Proceeds</Table.Cell>
                <Table.Cell>{formatCurrency(grossProceeds)}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell fontWeight="semibold">Optionee Net Proceeds</Table.Cell>
                <Table.Cell>{formatCurrency(wmwmOptioneeNet)}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell fontWeight="semibold">Total ESO Proceeds</Table.Cell>
                <Table.Cell>{formatCurrency(wmwmTotalEsoProceeds)}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell fontWeight="semibold">Total ESO Profit</Table.Cell>
                <Table.Cell>{formatCurrency(wmwmTotalEsoProfit)}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell fontWeight="semibold">ESO ROI</Table.Cell>
                <Table.Cell>{wmwmEsoRoi !== null ? `${wmwmEsoRoi.toFixed(4)}x` : "—"}</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table.Root>

          <Box
            cursor="pointer"
            onClick={() => setShowWmwmDetails(!showWmwmDetails)}
            p={3}
            borderRadius="md"
            border="1px"
            borderColor="border.default"
            _hover={{ bg: "hover.bg" }}
          >
            <Text fontSize="xs" fontWeight="semibold" color="text.muted" mb={showWmwmDetails ? 2 : 0}>
              {showWmwmDetails ? "▾" : "▸"} Breakdown
            </Text>
            {showWmwmDetails && (
              <Stack gap={1} mt={1}>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">ESO Participation</Text>
                  <Text fontSize="xs" fontWeight="semibold">{formatCurrency(wmwmEsoParticipation)}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">Net After ESO Participation</Text>
                  <Text fontSize="xs" fontWeight="semibold">{formatCurrency(wmwmNetAfterEso)}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">Liquidation Preference</Text>
                  <Text fontSize="xs" fontWeight="semibold">{formatCurrency(wmwmLiqPref)}</Text>
                </HStack>
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" color="text.muted">Backend Net Proceeds to Optionee</Text>
                  <Text fontSize="xs" fontWeight="semibold">{formatCurrency(wmwmBackendNet)}</Text>
                </HStack>
              </Stack>
            )}
          </Box>
        </Stack>

        {wmwmChartDataActive.length > 0 && (
          <Box flex={1} minW="400px">
            <Text fontSize="sm" fontWeight="semibold" textAlign="center" mb={2} color={wmwmViewIncentive ? "purple.600" : "text.muted"} _dark={{ color: wmwmViewIncentive ? "purple.300" : "text.muted" }}>
              {wmwmViewIncentive
                ? `Incentive Return vs. Exit Share Price (${INCENTIVE_DEADLINE_LABELS[incentiveDeadline] || incentiveDeadline || "No deadline"})`
                : "Total Return vs. Exit Share Price"}
            </Text>
            <Box h="300px">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={wmwmChartDataActive} margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="priceLabel" fontSize={10} />
                <YAxis fontSize={10} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatCurrency(v as number)} labelFormatter={(label) => `Price/Share: ${label}`} />
                <Legend fontSize={10} />
                <Line type="monotone" dataKey="optioneeNetProceeds" name="Optionee Net Proceeds" stroke={wmwmViewIncentive ? "#9F7AEA" : "#3182CE"} strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="totalEsoProfit" name="Total ESO Profit" stroke="#E53E3E" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
            </Box>
          </Box>
        )}
      </HStack>

      <Text fontSize="xs" color="text.muted" mt={4}>
        Last modified: {formatDate(offer?.updatedAt)}
      </Text>
    </Box>
  )
}

export const DealParamsView = ({ deal, company, onRefreshGrants, isRefreshingGrants, hideConfigTable }: Props) => {
  const queryClient = useQueryClient()
  const { underwritingLrp, capTableNotes, fmvs, yearsUntilExit } = company
  const minPpSplit = yearsUntilExit <= 2 ? 0 : (yearsUntilExit >= 8 ? 1 : yearsUntilExit / 7)
  const mostRecentLrp = parseFloat(capTableNotes?.esoLrp || "0") || 0

  const CLOSING_STAGE_IDS = [1, 2, 3, 4, 5, 10]
  const closingLtvData = useMemo(() => {
    return company.deals
      .filter((d) => CLOSING_STAGE_IDS.includes(d.dealStageId) && d.computedLtv != null && d.computedLtv > 0)
      .map((d) => ({ dealId: d.id, ltv: d.computedLtv! }))
  }, [company.deals])

  const { mostRecentFmv, fmvPercentChange } = useMemo(() => {
    const type1Fmvs = fmvs.filter((f) => f.fmvTypeId === 1 && f.value && f.date)
    if (type1Fmvs.length === 0) return { mostRecentFmv: 0, fmvPercentChange: null as number | null }
    type1Fmvs.sort((a, b) => new Date(b.date!).getTime() - new Date(a.date!).getTime())
    const latest = parseFloat(type1Fmvs[0].value!) || 0
    const previousFmv = type1Fmvs.find((f) => f.value !== type1Fmvs[0].value)
    let pctChange: number | null = null
    if (previousFmv) {
      const prev = parseFloat(previousFmv.value!) || 0
      if (prev !== 0) pctChange = ((latest - prev) / prev) * 100
    }
    return { mostRecentFmv: latest, fmvPercentChange: pctChange }
  }, [fmvs])

  // Staged grants — portal-uploaded, awaiting DM confirmation
  const stagedGrants = useMemo<DealGrant[]>(() => {
    return deal.grants.filter((g: DealGrant) => g.isStaged)
  }, [deal.grants])

  const [isConfirmingStaged, setIsConfirmingStaged] = useState(false)

  const handleConfirmStaged = async (grantIds?: number[]) => {
    setIsConfirmingStaged(true)
    try {
      await confirmStagedGrants(deal.id, grantIds)
      queryClient.invalidateQueries({ queryKey: ["dealInfo", String(deal.id)] })
      queryClient.invalidateQueries({ queryKey: ["dealLogs", String(deal.id)] })
      toaster.create({ title: grantIds ? "Grant confirmed" : "All staged grants confirmed", type: "success" })
    } catch {
      toaster.create({ title: "Failed to confirm grants", type: "error" })
    } finally {
      setIsConfirmingStaged(false)
    }
  }

  // Build grant list from deal.grants (confirmed only) with local include state
  const initialGrants = useMemo<GrantItem[]>(() => {
    return deal.grants
      .filter((g: DealGrant) => !g.isStaged)
      .map((g: DealGrant) => ({
        id: g.id,
        grantNumber: g.grantNumber,
        grantType: g.grantType,
        description: g.description,
        vested: g.vested,
        price: String(g.price ?? 0),
        grantDate: g.grantDate,
        include: true,
        tax: String(g.tax ?? 0),
      }))
  }, [deal.grants])

  const [grants, setGrants] = useState<GrantItem[]>(initialGrants)

  // Offer state — default to staged offer or first offer
  const offers = deal.offers
  const [selectedOffer, setSelectedOffer] = useState<DealOffer | null>(null)

  useEffect(() => {
    if (selectedOffer) return
    if (offers.length > 0) {
      const staged = offers.find((o) => o.isStaged)
      setSelectedOffer(staged || offers[0])
    } else {
      handleNewOffer()
    }
  }, [offers, selectedOffer])

  // Re-sync selectedOffer when server data updates (e.g. after Overview config auto-save)
  useEffect(() => {
    if (!selectedOffer || selectedOffer.id === 0) return
    const updated = offers.find((o) => o.offerNumber === selectedOffer.offerNumber)
    if (updated && updated.updatedAt !== selectedOffer.updatedAt) {
      setSelectedOffer(updated)
    }
  }, [offers]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync grants from deal data + apply offer inclusion in one pass
  // This ensures new grants (e.g. after extraction) always appear with correct include state
  useEffect(() => {
    const offerGrants = selectedOffer?.offerGrants
    setGrants(
      initialGrants.map((g) => {
        if (!offerGrants || offerGrants.length === 0) return { ...g, include: true }
        const match = offerGrants.find((og) => og.grantId === g.id)
        return { ...g, include: match ? match.included === 1 : true }
      })
    )
  }, [initialGrants, selectedOffer])

  const toggleInclude = (grantNumber: number) => {
    setGrants((prev) => {
      const updated = prev.map((g) =>
        g.grantNumber === grantNumber ? { ...g, include: !g.include } : g
      )
      // Fire API for saved offers (id !== 0)
      if (selectedOffer && selectedOffer.id !== 0) {
        const grant = updated.find((g) => g.grantNumber === grantNumber)
        if (grant) {
          upsertOfferGrant(selectedOffer.id, grant.id, grant.include ? 1 : 0).catch((err) =>
            console.error("Failed to upsert offer grant:", err)
          )
        }
      }
      return updated
    })
  }

  // Derived grant values
  const totalIsoExercise = useMemo(() => {
    return grants
      .filter((g) => g.grantType.toUpperCase() === "ISO" && g.include)
      .reduce((sum, g) => sum + parseFloat(g.price) * g.vested, 0)
  }, [grants])

  const totalNsoExercise = useMemo(() => {
    return grants
      .filter((g) => g.grantType.toUpperCase() === "NSO" && g.include)
      .reduce((sum, g) => sum + parseFloat(g.price) * g.vested, 0)
  }, [grants])

  const nsoWithholdingTax = useMemo(() => {
    return grants
      .filter((g) => g.grantType.toUpperCase() === "NSO" && g.include && g.tax !== "0")
      .reduce((sum, g) => sum + (parseFloat(g.tax) || 0), 0)
  }, [grants])

  const initialPayment = totalIsoExercise + totalNsoExercise + nsoWithholdingTax

  // Determine next offer number
  const nextOfferNumber = useMemo(() => {
    if (offers.length === 0) return 1
    const maxOffer = Math.max(...offers.map((o) => o.offerNumber))
    return maxOffer + 1
  }, [offers])

  const handleNewOffer = () => {
    setGrants((prev) => prev.map((g) => ({ ...g, include: true })))
    // Inherit config from staged offer (offer 0) when available
    const base = offers.find((o) => o.offerNumber === 0) || offers.find((o) => o.isStaged) || null
    const newOffer: DealOffer = {
      id: 0,
      dealId: deal.id,
      offerNumber: nextOfferNumber,
      offerType: base?.offerType || "Traditional",
      isStaged: 0,
      exerciseCost: null,
      desiredLiquidity: base?.desiredLiquidity ?? null,
      allocationAgreement: base?.allocationAgreement || "PPA",
      pp: 0.5,
      ppSplit: 0,
      isPreferentialReturn: base?.isPreferentialReturn ?? 0,
      lp: 0,
      optionsDocFee: base?.optionsDocFee ?? 0,
      upFrontFee: base?.upFrontFee ?? 0,
      ltv: 1,
      hypotheticalSharePrice: Math.round(2 * mostRecentLrp),
      isIncentive: base?.isIncentive ?? 0,
      incentivePp: base?.incentivePp ?? null,
      incentivePpSplit: base?.incentivePpSplit ?? null,
      incentiveLp: base?.incentiveLp ?? null,
      incentiveDeadline: base?.incentiveDeadline ?? null,
      exchangeRate: base?.exchangeRate ?? 1,
      isStagedInPortal: false,
      isAccepted: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }
    setSelectedOffer(newOffer)
  }

  const handleSelectOffer = (offerNum: string) => {
    if (offerNum === "new") {
      handleNewOffer()
      return
    }
    const selected = offers.find((o) => String(o.offerNumber) === offerNum)
    setSelectedOffer(selected || null)
  }

  return (
    <Box w="100%" p={5} border="1px" borderColor="border.default" borderRadius={10} boxShadow="sm" bg="card.bg">
      {/* Staged Grants Panel — shown when portal-uploaded grants await DM review */}
      {stagedGrants.length > 0 && (
        <Box mb={6} p={4} borderRadius={8} border="1px solid" borderColor="orange.300" bg="orange.50" _dark={{ bg: "orange.900", borderColor: "orange.600" }}>
          <HStack justifyContent="space-between" mb={3}>
            <HStack gap={2}>
              <Text fontSize="sm" fontWeight="bold" color="orange.700" _dark={{ color: "orange.200" }}>
                Staged Grants — Awaiting Review
              </Text>
              <Text fontSize="xs" color="orange.600" _dark={{ color: "orange.300" }}>
                ({stagedGrants.length} auto-extracted from portal upload)
              </Text>
            </HStack>
            <Button
              size="xs"
              colorPalette="orange"
              onClick={() => handleConfirmStaged()}
              loading={isConfirmingStaged}
            >
              Confirm All
            </Button>
          </HStack>
          <HStack overflowX="auto" pb={2} gap={3}>
            {stagedGrants.map((grant) => (
              <Stack
                key={`staged-${grant.id}`}
                minW="200px"
                p={4}
                borderRadius={10}
                bg={getGrantBg(grant.grantType)}
                boxShadow="sm"
                gap={1}
                border="2px solid"
                borderColor="orange.400"
                opacity={0.9}
              >
                <HStack justifyContent="space-between">
                  <Text fontSize="xs" fontWeight="bold" color="gray.700" truncate maxW="120px">
                    {grant.description || `Grant #${grant.grantNumber}`}
                  </Text>
                  <Text fontSize="xs" fontWeight="bold" color="gray.600">{grant.grantType.toUpperCase()}</Text>
                </HStack>
                <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                  {grant.vested.toLocaleString()} vested
                </Text>
                <Text fontSize="xs" color="gray.600">Strike: ${grant.price.toFixed(4)}</Text>
                <Text fontSize="xs" color="gray.500">{grant.grantDate || "—"}</Text>
                <Button
                  size="xs"
                  colorPalette="orange"
                  variant="outline"
                  mt={1}
                  onClick={() => handleConfirmStaged([grant.id])}
                  loading={isConfirmingStaged}
                >
                  Confirm
                </Button>
              </Stack>
            ))}
          </HStack>
        </Box>
      )}

      {/* Grant Schedule */}
      <HStack mb={4} gap={2} justifyContent="space-between">
        <HStack gap={2}>
          <Text fontSize="lg" fontWeight="bold">
            Grant Schedule
          </Text>
          <IconButton
            aria-label="Add grant"
            size="xs"
            variant="ghost"
          >
            <FaPlus size={10} />
          </IconButton>
          {onRefreshGrants && (
            <IconButton
              aria-label="Refresh grants from documents"
              size="xs"
              variant="ghost"
              onClick={onRefreshGrants}
              disabled={isRefreshingGrants}
            >
              {isRefreshingGrants ? <Spinner size="xs" /> : <FaSync size={12} />}
            </IconButton>
          )}
        </HStack>
      </HStack>

      <HStack overflowX="auto" pb={2} gap={3} mb={6}>
        {grants.map((grant) => (
          <GrantCard
            key={`${grant.grantType}-${grant.grantNumber}`}
            grant={grant}
            mostRecentFmv={mostRecentFmv}
            onToggleInclude={toggleInclude}
            onSaveGrant={async (grantId, data) => {
              try {
                await updateDealGrant(grantId, data)
                queryClient.invalidateQueries({ queryKey: ["dealInfo", String(deal.id)] })
                queryClient.invalidateQueries({ queryKey: ["dealLogs", String(deal.id)] })
                toaster.create({ title: "Grant updated", type: "success" })
              } catch {
                toaster.create({ title: "Failed to update grant", type: "error" })
                throw new Error("Failed")
              }
            }}
          />
        ))}
      </HStack>
      {/* Exercise Summary */}
      <HStack gap={6} my={6} flexWrap="wrap">
        <Stack gap={0}>
          <Text fontSize="xs" color="text.muted" fontWeight="medium">Total ISO Exercise</Text>
          <Text fontSize="md" fontWeight="bold">{formatCurrency(totalIsoExercise)}</Text>
        </Stack>
        <Stack gap={0}>
          <Text fontSize="xs" color="text.muted" fontWeight="medium">Total NSO Exercise</Text>
          <Text fontSize="md" fontWeight="bold">{formatCurrency(totalNsoExercise)}</Text>
        </Stack>
        <Stack gap={0}>
          <Text fontSize="xs" color="text.muted" fontWeight="medium">NSO Withholding Tax</Text>
          <Text fontSize="md" fontWeight="bold">{formatCurrency(nsoWithholdingTax)}</Text>
        </Stack>
        <Stack gap={0}>
          <Text fontSize="xs" color="text.muted" fontWeight="medium">Initial Payment</Text>
          <Text fontSize="md" fontWeight="bold">{formatCurrency(initialPayment)}</Text>
        </Stack>
      </HStack>
      {/* Offer Generator */}
      <OfferView
        offer={selectedOffer}
        offers={offers}
        grants={grants}
        totalIsoExercise={totalIsoExercise}
        totalNsoExercise={totalNsoExercise}
        nsoWithholdingTax={nsoWithholdingTax}
        mostRecentLrp={mostRecentLrp}
        mostRecentFmv={mostRecentFmv}
        minPpSplit={minPpSplit}
        underwritingLrp={underwritingLrp}
        closingLtvData={closingLtvData}
        onSelectOffer={handleSelectOffer}
        onNewOffer={handleNewOffer}
        onStageOffer={async (offerId, isStagedInPortal) => {
          await stageOfferInPortal(offerId, isStagedInPortal)
          queryClient.invalidateQueries({ queryKey: ["dealInfo", String(deal.id)] })
          queryClient.invalidateQueries({ queryKey: ["dealLogs", String(deal.id)] })
          const action = isStagedInPortal ? "staged in" : "unstaged from"
          toaster.create({
            title: `Offer ${selectedOffer?.offerNumber} ${action} portal`,
            type: "success",
          })
        }}
        onOfferSaved={() => {
          queryClient.invalidateQueries({ queryKey: ["dealInfo", String(deal.id)] })
          queryClient.invalidateQueries({ queryKey: ["dealLogs", String(deal.id)] })
        }}
        dealId={deal.id}
        hideConfigTable={hideConfigTable}
      />
    </Box>
  )
}
